(() => {
  'use strict';
  const fwFilmStrings = {"en":{"loadError":"The video could not be loaded. Please try again.","startHint":"Tap the speaker icon to start.","startError":"The video could not be started. Tap the speaker icon again.","enableSound":"Turn sound on","disableSound":"Turn sound off"},"nl":{"loadError":"De video kon niet worden geladen. Probeer het opnieuw.","startHint":"Tik op het luidsprekerpictogram om te starten.","startError":"De video kon niet worden gestart. Tik opnieuw op het luidsprekerpictogram.","enableSound":"Geluid inschakelen","disableSound":"Geluid uitschakelen"},"es":{"loadError":"No se pudo cargar el vídeo. Vuelve a intentarlo.","startHint":"Pulsa el icono del altavoz para iniciar el vídeo.","startError":"No se pudo iniciar el vídeo. Pulsa de nuevo el icono del altavoz.","enableSound":"Activar sonido","disableSound":"Desactivar sonido"},"pl":{"loadError":"Nie udało się załadować filmu. Spróbuj ponownie.","startHint":"Dotknij ikony głośnika, aby uruchomić film.","startError":"Nie udało się uruchomić filmu. Dotknij ponownie ikony głośnika.","enableSound":"Włącz dźwięk","disableSound":"Wyłącz dźwięk"}};
  const fwFilmText = (key, fallback) => fwFilmStrings[document.documentElement.lang.toLowerCase().split('-')[0]]?.[key] ?? fallback;

  if (customElements.get('fw-brand-film')) return;

  class FeinwaelderBrandFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      // Deferred scripts run while readyState is already "interactive", before
      // the later popup script has announced its pending 500 ms opening.
      if (document.readyState !== 'complete') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.controller.signal });
      } else this.initialize();
    }

    initialize() {
      if (!this.isConnected || this.video) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.poster = this.querySelector('[data-film-poster]');
      const options = { signal: this.controller.signal };
      this.controls = this.querySelector('.fw-brand-film__controls');
      this.soundButton = this.querySelector('[data-film-sound]');
      this.status = this.querySelector('[data-film-status]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.wantsPlay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      this.inView = this.getBoundingClientRect().bottom > 0 && this.getBoundingClientRect().top < window.innerHeight;
      this.loaded = false;
      this.hasEnabledSound = false;
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.controls = false;
      this.controls.hidden = false;

      this.soundButton.addEventListener('click', () => {
        const enable = this.video.muted;
        this.video.muted = !enable;
        this.wantsPlay = true;
        if (enable) {
          if (!this.hasEnabledSound && this.loaded) this.video.currentTime = 0;
          this.hasEnabledSound = true;
        }
        this.paint();
        this.syncPlayback();
      }, options);
      for (const type of ['play', 'pause', 'volumechange', 'ended']) {
        this.video.addEventListener(type, () => this.paint(), options);
      }
      this.video.addEventListener('playing', () => {
        if (this.poster) this.poster.hidden = true;
        this.setStatus('');
      }, options);
      this.video.addEventListener('error', () => {
        if (this.poster) this.poster.hidden = false;
        this.wantsPlay = false;
        this.setStatus((document.documentElement.lang.toLowerCase().startsWith('fr') ? "Impossible de charger la vidéo. Veuillez réessayer." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Impossibile caricare il video. Riprova." : fwFilmText('loadError', 'Der Film konnte nicht geladen werden. Bitte versuchen Sie es erneut.'))));
        this.paint();
      }, options);
      document.addEventListener('fw:restart-notice-state', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      document.addEventListener('fullscreenchange', () => this.syncPlayback(), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) { this.wantsPlay = false; this.syncPlayback(); }
      }, options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.paint();
      this.syncPlayback();
    }

    noticeBlocksPlayback() {
      return [...document.querySelectorAll('fw-restart-notice')].some(notice =>
        notice.dataset.playbackState === 'pending' || notice.dataset.playbackState === 'open'
        || Boolean(notice.querySelector('dialog[open]')));
    }

    syncPlayback() {
      if (!this.video) return;
      if (!this.wantsPlay || (!this.inView && !this.contains(document.fullscreenElement)) || document.hidden || this.noticeBlocksPlayback()) {
        this.video.pause();
        return;
      }
      if (!this.loaded) {
        this.video.querySelectorAll('source[data-film-src]').forEach(source => { source.src = source.dataset.filmSrc; });
        this.loaded = true;
        this.video.load();
      } else if (this.video.error) this.video.load();
      if (!this.video.paused) return;
      this.video.play().catch(error => {
        if (error.name === 'AbortError' || !this.isConnected) return;
        this.wantsPlay = false;
        this.setStatus(error.name === 'NotAllowedError' ? (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Appuyez sur l’icône du haut-parleur pour démarrer la vidéo." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Tocca l’icona dell’altoparlante per avviare il video." : fwFilmText('startHint', 'Zum Starten auf das Lautsprechersymbol tippen.'))) : (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Impossible de démarrer la vidéo. Appuyez à nouveau sur l’icône du haut-parleur." : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Impossibile avviare il video. Tocca nuovamente l’icona dell’altoparlante." : fwFilmText('startError', 'Der Film konnte nicht gestartet werden. Bitte erneut auf das Lautsprechersymbol tippen.'))));
        this.paint();
      });
    }

    setStatus(message) {
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    paint() {
      const soundLabel = this.video.muted ? (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Activer le son" : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Attiva l’audio" : fwFilmText('enableSound', 'Ton einschalten'))) : (document.documentElement.lang.toLowerCase().startsWith('fr') ? "Désactiver le son" : (document.documentElement.lang.toLowerCase().startsWith('it') ? "Disattiva l’audio" : fwFilmText('disableSound', 'Ton ausschalten')));
      this.soundButton.setAttribute('aria-pressed', String(!this.video.muted));
      this.soundButton.setAttribute('aria-label', soundLabel);
      this.soundButton.setAttribute('title', soundLabel);
      this.querySelector('[data-film-mute-icon]').toggleAttribute('hidden', !this.video.muted);
      this.querySelector('[data-film-audio-icon]').toggleAttribute('hidden', this.video.muted);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.video?.pause();
      this.controller = null;
      this.video = null;
    }
  }
  customElements.define('fw-brand-film', FeinwaelderBrandFilm);
})();
